/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Button} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor, dlsLayout} from '@ion/dls/src/theme/theme';
import type {FloatingIconButtonGroupProps, FloatingIconButtonProps} from '../FloatingIconButton';

type StyleProps = {
  round?: boolean;
  backgroundColor?: string;
};

const useStyles = makeStyles(() => {
  const iconSize = dlsAlt('components', 'btn', 'floatingIconSize');
  return ({
    root : {
      background   : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('white'),
      height       : iconSize,
      width        : iconSize,
      minWidth     : iconSize,
      padding      : dlsLayout('spacing'),
      borderRadius : ({round}: StyleProps) => round ? '50% !important' : 1,
      boxShadow    : 'none',
      '&:active'   : {
        background : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('grey1')
      },
      '&:hover' : {
        background : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('paleBlue'),
        boxShadow  : 'none'
      },
      '&$disabled' : {
        background : dlsColor('white'),
        boxShadow  : 'none'
      }
    },
    shimmer : {
      position   : 'relative',
      overflow   : 'hidden',
      '&::after' : {
        content    : '""',
        position   : 'absolute',
        top        : 0,
        left       : '-120%',
        width      : '140%',
        height     : '100%',
        background : `linear-gradient(
          120deg,
          transparent 0%,
          ${dlsColor('lightTeal')} 50%,
          transparent 100%
        )`,
        animation     : '$shimmerMove 2s infinite',
        pointerEvents : 'none'
      }
    },
    '@keyframes shimmerMove' : {
      '0%' : {
        transform : 'translateX(0)',
        opacity   : 0.80
      },
      '50%' : {
        transform : 'translateX(300%)',
        opacity   : 0.80
      },
      '50.01%, 100%' : {
        transform  : 'translateX(-120%)',
        opacity    : 0,
        background : 'transparent'
      }
    },
    disabled : {}
  });
});

export const FloatingIconButton = ({
  children,
  label,
  tooltipLabel,
  isGrouped,
  backgroundColor,
  className,
  isSelected = false,
  shimmer = false,
  ...props
}: FloatingIconButtonProps) => {
  const classes = useStyles({round: props?.round, backgroundColor});
  const combinedClassName = clsx(classes.root, {[classes.shimmer]: shimmer}, className);

  return (
    <Button variant='contained'
      classes={{
        root     : combinedClassName,
        disabled : classes.disabled
      }}
      style={{...(isSelected && {backgroundColor: dlsColor('grey1')})}}
      {...props}
      color='primary'>
      {React.cloneElement(children, {
        color : props.color ? props.color
          : props.disabled
            ? dlsColor('grey4')
            : isSelected
              ? dlsColor('grey6')
              : dlsColor('caBlue')
      })}
      {label}
    </Button>
  );
};

type FloatingIconButtonGroupComponent = React.FC<FloatingIconButtonGroupProps> & {
  FloatingIconButton: React.ComponentType<FloatingIconButtonProps>;
};

export const FloatingIconButtonGroup = (({
  children,
  isVertical,
  noShadow,
  style = {}
}: FloatingIconButtonGroupProps) => {
  return (
    <div style={{
      boxShadow     : noShadow ? 'none' : '0px 2px 10px rgba(0, 0, 0, 0.2)',
      display       : 'flex',
      flexDirection : isVertical ? 'column' : 'row',
      ...style as GenericObject
    }}>
      {children}
    </div>
  );
}) as FloatingIconButtonGroupComponent;

FloatingIconButtonGroup.FloatingIconButton = FloatingIconButton;

export default FloatingIconButton;
